import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Minus, RefreshCw } from "lucide-react";
import { fmt } from "@/hooks/useRates";
import { useLang } from "@/i18n";
import ShareRatesButton from "@/components/ShareRatesButton";

const FEATURED = new Set(["COPPER", "ALUMINIUM"]);

const TrendBadge = ({ change, pct, testid }) => {
  const color = change > 0 ? "text-[#00C851]" : change < 0 ? "text-[#FF3B30]" : "text-white/40";
  const Icon = change > 0 ? TrendingUp : change < 0 ? TrendingDown : Minus;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 ${color}`}>
      <Icon size={13} />
      {change >= 0 ? "+" : ""}
      {pct.toFixed(2)}%
    </span>
  );
};

const FlashPrice = ({ value, trend, dec, prefix, testid }) => (
  <motion.span
    key={value}
    data-testid={testid}
    initial={{ color: trend === "down" ? "#FF3B30" : trend === "up" ? "#00C851" : "#FDFDFD" }}
    animate={{ color: "#FDFDFD" }}
    transition={{ duration: 1.4, ease: "easeOut" }}
    className="font-semibold"
  >
    {prefix}
    {fmt(value, dec)}
  </motion.span>
);

export default function RatesSection({ rates, updatedAt, usdinr, source }) {
  const { str } = useLang();
  return (
    <section
      id="rates"
      data-testid="rates-section"
      className="relative px-6 sm:px-10 lg:px-16 py-24 lg:py-32 border-b border-white/10"
    >
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12 lg:mb-16">
        <div>
          <p className="font-ticker text-[11px] tracking-[0.4em] uppercase text-[#25D366] mb-4">
            {str.ratesTag}
          </p>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none">
            MCX <span className="text-stroke-faint">/</span> LME{" "}
            <span className="text-[#25D366]">Rates</span>
          </h2>
        </div>
        <div className="flex flex-col items-start lg:items-end gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            {usdinr && (
              <span
                data-testid="usdinr-chip"
                className="font-ticker text-xs font-bold bg-[#25D366] text-[#0A0A0A] px-4 py-2"
              >
                1 USD = ₹{fmt(usdinr, 2)}
              </span>
            )}
            <ShareRatesButton rates={rates} usdinr={usdinr} />
          </div>
          <div className="flex items-center gap-2 font-ticker text-[11px] tracking-[0.2em] uppercase text-white/40">
            <RefreshCw size={12} className="animate-[spin_4s_linear_infinite] text-[#25D366]" />
            <span data-testid="rates-updated-at">
              {source === "live" ? str.live : str.feed} · {updatedAt ? new Date(updatedAt).toLocaleTimeString("en-IN") : "…"}
            </span>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="hidden md:block overflow-x-auto border border-white/10 bg-[#121212]/60"
      >
        <table className="w-full min-w-[760px] text-left font-ticker text-sm" data-testid="rates-table">
          <thead>
            <tr className="border-b border-white/10 text-[10px] tracking-[0.3em] uppercase text-white/40">
              <th className="px-6 py-5 font-medium">{str.thMetal}</th>
              <th className="px-6 py-5 font-medium">{str.thMcx}</th>
              <th className="px-6 py-5 font-medium">{str.thChange}</th>
              <th className="px-6 py-5 font-medium">{str.thLme}</th>
              <th className="px-6 py-5 font-medium">{str.thChange}</th>
              <th className="px-6 py-5 font-medium text-right">{str.thUnit}</th>
            </tr>
          </thead>
          <tbody>
            {rates.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-white/40 tracking-[0.2em] uppercase text-xs">
                  {str.loadingFeed}
                </td>
              </tr>
            )}
            {rates.map((r, i) => (
              <motion.tr
                key={r.symbol}
                data-testid={`rate-row-${r.symbol.toLowerCase()}`}
                initial={{ opacity: 0, x: -24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.06, ease: "easeOut" }}
                className={`border-b border-white/5 last:border-0 transition-colors duration-300 ${
                  FEATURED.has(r.symbol) ? "bg-[#25D366]/[0.06] hover:bg-[#25D366]/[0.1]" : "hover:bg-white/[0.03]"
                }`}
              >
                <td className="px-6 py-5">
                  <span className="flex items-center gap-2">
                    <span className="text-[#FDFDFD] font-semibold tracking-[0.15em]">{r.symbol}</span>
                    {FEATURED.has(r.symbol) && (
                      <span className="text-[8px] tracking-[0.2em] uppercase bg-[#25D366] text-[#0A0A0A] px-1.5 py-0.5 font-bold">
                        {str.star}
                      </span>
                    )}
                  </span>
                  <span className="block text-[10px] tracking-[0.2em] text-white/35 uppercase mt-1">{r.name}</span>
                </td>
                <td className="px-6 py-5 text-base">
                  <FlashPrice value={r.mcx} trend={r.trend} dec={r.dec} prefix="₹" testid={`mcx-price-${r.symbol.toLowerCase()}`} />
                </td>
                <td className="px-6 py-5">
                  <TrendBadge change={r.mcx_change} pct={r.mcx_change_pct} testid={`mcx-change-${r.symbol.toLowerCase()}`} />
                </td>
                <td className="px-6 py-5 text-base">
                  {r.lme != null ? (
                    <FlashPrice value={r.lme} trend={r.trend} dec={r.lme_dec} prefix="$" testid={`lme-price-${r.symbol.toLowerCase()}`} />
                  ) : (
                    <span className="text-white/30">—</span>
                  )}
                </td>
                <td className="px-6 py-5">
                  <TrendBadge change={r.lme_change} pct={r.lme_change_pct} testid={`lme-change-${r.symbol.toLowerCase()}`} />
                </td>
                <td className="px-6 py-5 text-right text-[10px] tracking-[0.2em] text-white/35 uppercase">
                  {r.mcx_unit}
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </motion.div>

      <div className="md:hidden space-y-3" data-testid="rates-cards-mobile">
        {rates.length === 0 && (
          <p className="border border-white/10 bg-[#121212]/60 px-4 py-8 text-center font-ticker text-xs tracking-[0.2em] uppercase text-white/40">
            {str.loadingFeed}
          </p>
        )}
        {rates.map((r) => (
          <div
            key={r.symbol}
            data-testid={`rate-card-${r.symbol.toLowerCase()}`}
            className={`border px-4 py-4 ${
              FEATURED.has(r.symbol) ? "border-[#25D366]/40 bg-[#25D366]/[0.06]" : "border-white/10 bg-[#121212]/60"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 font-ticker">
                <span className="text-sm font-semibold tracking-[0.15em] text-[#FDFDFD]">{r.symbol}</span>
                {FEATURED.has(r.symbol) && (
                  <span className="text-[8px] tracking-[0.2em] uppercase bg-[#25D366] text-[#0A0A0A] px-1.5 py-0.5 font-bold">
                    {str.star}
                  </span>
                )}
              </span>
              <span className="font-ticker text-xs">
                <TrendBadge change={r.mcx_change} pct={r.mcx_change_pct} testid={`mcx-change-m-${r.symbol.toLowerCase()}`} />
              </span>
            </div>
            <div className="mt-3 flex items-end justify-between gap-3 font-ticker">
              <div>
                <p className="text-[9px] tracking-[0.25em] uppercase text-white/35 mb-0.5">MCX ₹</p>
                <p className="text-lg leading-none">
                  <FlashPrice value={r.mcx} trend={r.trend} dec={r.dec} prefix="₹" testid={`mcx-price-m-${r.symbol.toLowerCase()}`} />
                </p>
              </div>
              <div className="text-right">
                <p className="text-[9px] tracking-[0.25em] uppercase text-white/35 mb-0.5">LME $</p>
                <p className="text-sm text-white/70 leading-none">{r.lme != null ? `$${fmt(r.lme, r.lme_dec)}` : "—"}</p>
              </div>
              <p className="text-[9px] tracking-[0.2em] uppercase text-white/35">{r.mcx_unit}</p>
            </div>
          </div>
        ))}
      </div>

      <p className="mt-6 font-ticker text-[10px] tracking-[0.2em] uppercase text-white/30" data-testid="rates-disclaimer">
        {str.ratesDisclaimer}
      </p>
    </section>
  );
}
